import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/GlassBits";
import { formatDate } from "@/lib/format";
import { Plus, Copy, Trash2, X } from "lucide-react";

export default function ApiKeys() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", website: "", permissions: ["read", "write"] });

  const load = async () => {
    const { data } = await api.get("/admin/api-keys");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/api-keys", form);
      toast.success("API key created");
      setOpen(false);
      setForm({ name: "", website: "", permissions: ["read", "write"] });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key?")) return;
    await api.delete(`/admin/api-keys/${id}`);
    toast.success("Revoked");
    load();
  };

  return (
    <div>
      <SectionTitle eyebrow="Security" title="API Keys" right={<button onClick={() => setOpen(true)} className="ldc-btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="apik-new"><Plus size={14}/> New key</button>} />
      <div className="grid gap-3">
        {items.length === 0 && (<div className="ldc-glass p-8 text-center text-white/50">No keys yet. Issue one to enable /api/v1 access.</div>)}
        {items.map((k) => (
          <div key={k.id} className="ldc-glass p-5" data-testid={`apik-row-${k.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-display text-lg">{k.name}</div>
                <div className="text-xs text-white/40">Created {formatDate(k.created_at)}</div>
                <div className="mt-3 space-y-1">
                  <KeyRow label="API key" v={k.api_key} />
                  <KeyRow label="Secret" v={k.secret_key} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`ldc-badge ${k.status === "active" ? "ldc-badge-paid" : "ldc-badge-failed"}`}>{k.status}</span>
                <button onClick={() => revoke(k.id)} className="p-2 rounded hover:bg-white/5 text-white/60 hover:text-red-400 transition-colors" data-testid={`apik-revoke-${k.id}`} title="Revoke">
                  <Trash2 size={16}/>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={create} className="ldc-glass p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl">Issue API Key</h3>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-white/5" data-testid="apik-modal-close"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <F label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="apik-input-name" required />
              <F label="Website (optional)" v={form.website} on={(v) => setForm({ ...form, website: v })} testid="apik-input-website" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-white/10 text-sm">Cancel</button>
              <button type="submit" className="ldc-btn-primary px-5 py-2 rounded-md text-sm" data-testid="apik-submit">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function KeyRow({ label, v }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-16 text-white/40 uppercase tracking-[0.2em]">{label}</span>
      <code className="font-mono bg-white/5 px-2 py-1 rounded text-[#fbce18] break-all">{v}</code>
      <button onClick={() => { navigator.clipboard.writeText(v); toast.success("Copied"); }} className="p-1 rounded hover:bg-white/5" title="Copy"><Copy size={12}/></button>
    </div>
  );
}
function F({ label, v, on, testid, required }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">{label}</div>
      <input value={v} onChange={(e) => on(e.target.value)} required={required} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid={testid} />
    </label>
  );
}
